import { useEffect, useState } from "react";
import { useCompanyDashboardConfig } from "./useCompanyDashboardConfig";
import { useIsGrandParent } from "./useIsGrandParent";
import { getDashboardV2Preview } from "src/utils/featureFlags";

/**
 * Effective "is the V2 experience on" for the logged-in user.
 *
 * Resolution order:
 *  1. An explicit ?dashboard=v1 / ?dashboard=v2 preview override (live /
 *     session-scoped, never persisted) always wins, so a super admin /
 *     developer can preview either view without changing the company setting.
 *  2. The platform super admin — the Grand Parent company (companyType "GP") —
 *     always has V2 as part of the super-admin toolset, regardless of any
 *     single company's flag, mirroring the Failed Transactions gate. A Parent
 *     company's ROOT admin is NOT treated as a super admin here; it follows its
 *     own company flag like every other login.
 *  3. Every other (company / team) login follows the per-company flag read for
 *     their OWN company (Company → Settings), so turning V2 off for a company
 *     hides it for that company's users.
 *
 * NOTE: there is intentionally no "force V2 on in local dev" shortcut — that
 * masked the real per-company flag and made an "off" company still render V2
 * locally. Developers who want V2 locally use the ?dashboard=v2 preview.
 */
export const useDashboardV2Enabled = () => {
  const { data } = useCompanyDashboardConfig();
  const companyHasV2 = data?.dashboardV2Enabled === true;
  const isGrandParent = useIsGrandParent();

  const [preview, setPreview] = useState(null); // true | false | null
  useEffect(() => {
    setPreview(getDashboardV2Preview());
  }, []);

  if (preview !== null) return preview;
  if (isGrandParent) return true;
  return companyHasV2;
};

export default useDashboardV2Enabled;
